/** @jsxImportSource theme-ui @jsxRuntime classic */
import {
  Button,
  AnimationState,
  useAnimationStateSelector,
  usePageTransitionActions,
  usePageTransitionDispatch,
  SxStyleProp,
  useThemeUI,
  useBreakpointIndex,
} from '@tishman/components';
import * as Icons from '@tishman/icons';
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { animated, Controller, useSpring } from '@react-spring/web';

import type { ReactNode } from 'react';

/** Modal Container is used to wrap the contents of Modals. Includes the animation
 * and close button.
 */

interface ModalContainerProps {
  'children': React.ReactNode;
  'closeModal': () => void;
  'data-testid'?: string;
  'scrollable'?: boolean;
  'style'?: SxStyleProp;
}

export const ModalContainer = ({
  closeModal,
  children,
  'data-testid': dataTestId,
  scrollable = true,
  style,
}: ModalContainerProps): JSX.Element => {
  const actions = usePageTransitionActions();
  const dispatch = usePageTransitionDispatch();
  const themeUI = useThemeUI();
  const isMobile = useBreakpointIndex() < 2;

  const ctrlIds = useRef(new Set<number>());

  const [transition, ref] = useSpring((ctrl: Controller) => {
    ctrlIds.current.add(ctrl.id);

    return {
      x: '100%',
    };
  });

  useEffect(() => {
    const ctrlIds_ = ctrlIds.current;

    ctrlIds_.forEach((animationId) => {
      dispatch(
        actions.add({
          animationId,
          animationState: AnimationState.SHOULD_ENTER,
        }),
      );
    });

    return () => {
      ctrlIds_.forEach((animationId) => {
        dispatch(actions.remove(animationId));
      });
    };
  }, [actions, dispatch]);

  const animationState = useAnimationStateSelector([...ctrlIds.current]);

  useLayoutEffect(() => {
    switch (animationState) {
      case AnimationState.SHOULD_ENTER: {
        ref.start({
          onStart: (_, ctrl) => {
            dispatch(
              actions.set({
                animationState: AnimationState.IS_ENTERING,
                animationId: ctrl.id,
              }),
            );
          },
          onRest: (_, ctrl) => {
            dispatch(
              actions.set({
                animationId: ctrl.id,
                animationState: AnimationState.DID_ENTER,
              }),
            );
          },
          x: '0%',
        });
        break;
      }
      case AnimationState.SHOULD_LEAVE: {
        ref.start({
          onStart: (_, ctrl) => {
            dispatch(
              actions.set({
                animationId: ctrl.id,
                animationState: AnimationState.IS_LEAVING,
              }),
            );
          },
          onRest: (_, ctrl) => {
            dispatch(
              actions.set({
                animationId: ctrl.id,
                animationState: AnimationState.DID_LEAVE,
              }),
            );
          },
          x: '100%',
        });
        break;
      }
    }

    const ctrlIds_ = ctrlIds.current;

    return () => {
      dispatch(
        actions.set({
          animationId: ctrlIds_.values().next().value,
          animationState: AnimationState.DID_LEAVE,
        }),
      );
    };
  }, [actions, animationState, dispatch, ref]);

  return (
    <animated.div
      style={{
        ...transition,
        ...style,
        background: themeUI.theme.colors.background,
        position: 'relative',
        placeSelf: 'stretch right',
        width: isMobile ? '100%' : '90%',
        maxWidth: isMobile ? '100%' : 516,
        overflow: scrollable ? 'scroll' : 'unset',
      }}
    >
      <Button
        data-testid={dataTestId}
        onClick={closeModal}
        sx={{
          'color': 'text',
          'background': 'transparent',
          'position': 'absolute',
          'zIndex': 'overlay',
          'top': 0,
          'right': 0,
          'p': ['22px', 3],
          'border': 'none',
          'fontSize': '0px',
          '&:hover': {
            background: 'transparent !important',
            svg: {
              transform: 'scale(1.1)',
            },
          },
        }}
      >
        <Icons.Close
          style={{
            transition: 'transform 0.3s ease-in-out',
          }}
        />
      </Button>
      {children}
    </animated.div>
  );
};
